import { takeLatest, put, call, takeEvery, select } from 'redux-saga/effects';
import * as authSelector from 'src/module/authentication/selector';
import { Map } from 'immutable';

import * as api from 'src/service/api/checkSheet';
import * as checkSheetActions from './action';
import * as deviceActions from '../device/action';

import toast from 'src/utils/toast';
import { getTranslation } from 'src/i18n/translations';
import { FIELD_REPORT_FILE_SLUG_MAP } from 'src/screen/Protected/FieldReportListScreen/fieldReportTypes';

export function * checkSheetRoot () {
  yield takeEvery(checkSheetActions.FETCH_CHECK_SHEET_TASKS, fetchCheckSheetTasks);
  yield takeLatest(checkSheetActions.SAVE_CHECK_SHEET, saveCheckSheet);
  yield takeLatest(checkSheetActions.UPDATE_CHECK_SHEET, updateCheckSheet);
  yield takeLatest(checkSheetActions.FETCH_CHECK_SHEET, fetchCheckSheet);
  yield takeLatest(checkSheetActions.FETCH_FIELD_REPORTS, fetchFieldReports);
  yield takeLatest(checkSheetActions.LINK_DEVICE_TO_CHECK_SHEET, linkDeviceToCheckSheet);
  yield takeLatest(checkSheetActions.UPDATE_DEVICE_LINK, updateDeviceLink);
  yield takeLatest(checkSheetActions.DOWNLOAD_FIELD_REPORT_PDF, downloadFieldReportPdf);
  yield takeLatest(checkSheetActions.DOWNLOAD_BULK_FIELD_REPORT_PDF, downloadBulkFieldReportPdf);
  yield takeLatest(checkSheetActions.DOWNLOAD_FIELD_REPORT_SUMMARY_PDF, downloadFieldReportSummaryPdf);
  yield takeLatest(checkSheetActions.FETCH_REPORTS_CSV_EXPORT, fetchReportsCsvExport);
}
function * fetchCheckSheetTasks (action) {
  try {
    const tasks = yield call(api.getCheckSheetTasks, action);
    yield put(checkSheetActions.fetchCheckSheetTasksSuccess(tasks, action.checkSheetType));
  } catch (err: any) {
    yield put(checkSheetActions.fetchCheckSheetTasksFailed(Map(err)));
  }
}

function * saveCheckSheet (action) {
  try {
    const data = yield call(api.saveCheckSheet, action);
    yield put(checkSheetActions.saveCheckSheetSuccess(action.deviceId, action.checkSheetType, data));
    // add to device
    yield put(deviceActions.addDeviceCheckSheetSuccess(action.deviceId, action.checkSheetType, data.get('id')));
  } catch (err: any) {
    yield put(checkSheetActions.saveCheckSheetFailed(Map(err), action.checkSheetType));
    toast.error(getTranslation('toast.errorSavingCheckSheet'));
  }
}
function * updateCheckSheet (action) {
  try {
    const data = yield call(api.updateCheckSheet, action);
    yield put(checkSheetActions.updateCheckSheetSuccess(action.deviceId, data, action.checkSheetType));
    yield put(checkSheetActions.closeCheckSheet(action.checkSheetType));
  } catch (err: any) {
    yield put(checkSheetActions.updateCheckSheetFailed(Map(err), action.checkSheetType));
    toast.error(getTranslation('toast.errorSavingCheckSheet'));
  }
}
function * fetchCheckSheet (action) {
  try {
    const report = yield call(api.fetchCheckSheet, action);
    yield put(checkSheetActions.fetchCheckSheetSuccess( action.checkSheetType, report, action.checkSheetId));
  } catch (err: any) {
    yield put(checkSheetActions.fetchCheckSheetFailed(Map(err)));
    toast.error(getTranslation('toast.errorFetchingCheckSheet'));
  }
}

function triggerBlobDownload (response: any, filename: string, mimeType?: string) {
  const blob = mimeType ? new Blob([response], { type: mimeType }) : new Blob([response]);
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

function safeFacilityPrefix (facilityName: string | null) {
  return facilityName ? `${facilityName.replace(/[^a-z0-9]/gi, '-').toLowerCase()}-` : '';
}

function * downloadFieldReportPdf (action: any) {
  try {
    const facilityName = yield select(authSelector.getFacilityName);
    const response = yield call(api.downloadFieldReportPdf, action);
    yield put(checkSheetActions.downloadFieldReportPdfSuccess(action.reportId));
    const namePart = FIELD_REPORT_FILE_SLUG_MAP[action.checkSheetType] || action.checkSheetType;
    const tag = action.deviceTag ? `-${action.deviceTag}` : '';
    triggerBlobDownload(response, `${safeFacilityPrefix(facilityName)}${namePart}${tag}.pdf`);
  } catch (err: any) {
    yield put(checkSheetActions.downloadFieldReportPdfFailed(action.reportId));
    toast.error('Error generating commissioning PDF');
  }
}

function * downloadFieldReportSummaryPdf (action) {
  try {
    const facilityName = yield select(authSelector.getFacilityName);
    const response = yield call(api.downloadFieldReportSummaryPdf, action);
    yield put(checkSheetActions.downloadFieldReportSummaryPdfSuccess());
    const slug = action.params?.category === 'audit' ? 'audit-summary' : 'commissioning-summary';
    triggerBlobDownload(response, `${safeFacilityPrefix(facilityName)}${slug}.pdf`);
  } catch (err: any) {
    yield put(checkSheetActions.downloadFieldReportSummaryPdfFailed());
    toast.error('Error generating commissioning summary PDF');
  }
}

function * downloadBulkFieldReportPdf (action) {
  try {
    const facilityName = yield select(authSelector.getFacilityName);
    const response = yield call(api.downloadBulkFieldReportPdf, action);
    yield put(checkSheetActions.downloadBulkFieldReportPdfSuccess());
    triggerBlobDownload(response, `${safeFacilityPrefix(facilityName)}commissioning-reports.pdf`);
  } catch (err: any) {
    yield put(checkSheetActions.downloadBulkFieldReportPdfFailed());
    toast.error('Error generating commissioning PDF');
  }
}

function * fetchFieldReports (action) {
  try {
    const response = yield call(api.fetchFieldReports, action);
    yield put(checkSheetActions.fetchFieldReportsSuccess(
      response.get('data'),
      response.get('total'),
      response.get('page'),
      response.get('totalPages'),
    ));
  } catch (err: any) {
    toast.error(getTranslation('toast.errorFetchingCommissioningReports'));
    yield put(checkSheetActions.fetchFieldReportsFailed());
  }
}

function * linkDeviceToCheckSheet (action) {
  try {
    yield call(api.linkDeviceToCheckSheet, action);
    yield put(checkSheetActions.linkDeviceToCheckSheetSuccess(action.reportId, action.deviceId));
    toast.success(getTranslation('toast.deviceLinkedSuccess'));
  } catch (err: any) {
    toast.error(getTranslation('toast.errorLinkingDevice'));
    yield put(checkSheetActions.linkDeviceToCheckSheetFailed());
  }
}

function * updateDeviceLink (action) {
  try {
    yield call(api.updateDeviceLink, action);
    yield put(checkSheetActions.updateDeviceLinkSuccess(action.reportId, action.deviceId));
    // Refetch full check sheet so Redux has the updated extras (tag, commLoopNumber, etc.)
    yield put(checkSheetActions.fetchCheckSheet(action.reportId, action.checkSheetType));
    toast.success(getTranslation('toast.deviceLinkUpdatedSuccess'));
  } catch (err: any) {
    toast.error(getTranslation('toast.errorUpdatingDeviceLink'));
    yield put(checkSheetActions.updateDeviceLinkFailed());
  }
}

function * fetchReportsCsvExport (action) {
  try {
    const facilityName = yield select(authSelector.getFacilityName);
    const response = yield call(api.fetchReportsCsvExport, action);
    yield put(checkSheetActions.fetchReportsCsvExportSuccess());
    const slug = action.optionalFilters?.get('category') === 'audit' ? 'audit-reports' : 'commissioning-reports';
    triggerBlobDownload(response, `${safeFacilityPrefix(facilityName)}${slug}.csv`, 'text/csv');
    toast.success(getTranslation('toast.csvExportSuccess'));
  } catch (err: any) {
    yield put(checkSheetActions.fetchReportsCsvExportFailed(Map(err)));
    toast.error(getTranslation('toast.csvExportFailed'));
  }
}

export default checkSheetRoot;
